import React, { useMemo, useState } from 'react';
import { ThumbsUp, Trophy, Users, ExternalLink, Crown, Sparkles, RefreshCcw, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { VARIANTS } from '../mock';
import { toast } from '../hooks/use-toast';

export default function CommunityGallery({
  sprites,
  loading,
  userId,
  onVote,
  onRefresh,
  onUnpublish,
}) {
  const [submitOpen, setSubmitOpen] = useState(false);

  const top = sprites?.[0];
  const totalVotes = useMemo(
    () => sprites.reduce((acc, s) => acc + (s.vote_count || 0), 0),
    [sprites]
  );

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-6">
      <div
        className="rounded-2xl border-[3px] shadow-[0_5px_0_var(--t-card-border)] overflow-hidden"
        style={{ borderColor: 'var(--t-card-border)' }}
      >
        {/* Header banner */}
        <div
          className="p-3 sm:p-5 md:p-6 border-b-[3px] flex items-center justify-between flex-wrap gap-3"
          style={{
            background:
              'radial-gradient(circle at 0% 0%, #ff2bd6 0%, transparent 38%),' +
              'radial-gradient(circle at 100% 0%, #ffd900 0%, transparent 38%),' +
              'radial-gradient(circle at 50% 100%, #00b8ff 0%, transparent 50%),' +
              'linear-gradient(135deg, #3a1064 0%, #5b1ca8 100%)',
            borderColor: 'var(--t-card-border)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-[#ffd900] border-[3px] border-[#1a0440] flex items-center justify-center -rotate-6 shadow-[0_4px_0_#1a0440] shrink-0">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-[#3a1064]" strokeWidth={3} />
            </div>
            <div className="min-w-0">
              <div className="text-yellow-300 font-black text-lg sm:text-2xl leading-tight">
                Community Forge Gallery
              </div>
              <div className="text-white/80 text-xs sm:text-sm font-bold mt-0.5">
                {sprites.length} sprites · {totalVotes} total votes · top creation gets submitted to Epic Games
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              variant="outline"
              className="h-10 rounded-xl border-2 border-yellow-300 bg-white/15 hover:bg-white/25 text-yellow-300 font-black text-sm"
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {top && (
              <Button
                className="h-10 rounded-xl border-2 border-[#1a0440] bg-yellow-300 hover:bg-yellow-200 text-[#3a1064] font-black shadow-[0_3px_0_#1a0440] text-sm flex-1 sm:flex-initial"
                onClick={() => setSubmitOpen(true)}
              >
                <Trophy className="h-4 w-4 mr-2 shrink-0" />
                <span className="sm:hidden">Submit to Epic</span>
                <span className="hidden sm:inline">Submit Top Sprite to Epic</span>
              </Button>
            )}
          </div>
        </div>

        {/* Top sprite spotlight */}
        {top && (
          <div
            className="p-3 sm:p-5 md:p-6 border-b-2 border-[#3a1064]/15 flex items-center gap-3 sm:gap-4 flex-wrap"
            style={{
              background: 'linear-gradient(90deg, #fff3a0 0%, #ffd900 100%)',
            }}
          >
            <div className="relative shrink-0">
              <div
                className="h-20 w-20 sm:h-32 sm:w-32 rounded-xl sm:rounded-2xl border-[4px] border-[#3a1064] overflow-hidden bg-white shadow-[0_5px_0_#3a1064]"
              >
                <img src={top.data_url} alt={top.name} className="w-full h-full object-contain" />
              </div>
              <span className="absolute -top-2 -left-2 sm:-top-3 sm:-left-3 inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-[#3a1064] text-yellow-300 rounded-full border-2 border-yellow-300 font-black text-[10px] sm:text-[11px] uppercase tracking-wider shadow-[0_2px_0_#1a0440]">
                <Crown className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill="currentColor" /> #1
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[2px] sm:tracking-[3px] text-[#3a1064]/70 mb-0.5">
                Reigning champion
              </div>
              <div className="font-black text-base sm:text-2xl text-[#3a1064] break-words leading-tight">{top.name}</div>
              <div className="text-xs sm:text-sm font-bold text-[#3a1064]/80 mt-1 line-clamp-2">
                by <span className="font-black">{top.owner_name}</span> · {top.prompt || 'Custom sprite'}
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 sm:gap-2 bg-[#3a1064] text-yellow-300 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border-2 border-yellow-300 font-black shadow-[0_2px_0_#1a0440] text-xs sm:text-base">
                <ThumbsUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="currentColor" />
                {top.vote_count} {top.vote_count === 1 ? 'vote' : 'votes'}
              </div>
            </div>
          </div>
        )}

        {/* Sprites grid */}
        <div className="p-3 sm:p-5 md:p-6" style={{ background: 'var(--t-card)' }}>
          {sprites.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="h-10 w-10 mx-auto text-[#ff2bd6]" />
              <div className="mt-3 font-black text-lg" style={{ color: 'var(--t-text)' }}>
                The gallery is empty
              </div>
              <div className="text-sm font-bold mt-1" style={{ color: 'var(--t-text-soft)' }}>
                Forge a sprite below and click <span className="text-[#ff2bd6] font-black">Publish</span> to be the first.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-4">
              {sprites.map((s, i) => (
                <PublicSpriteCard
                  key={s.id}
                  sprite={s}
                  rank={i + 1}
                  userId={userId}
                  onVote={onVote}
                  onUnpublish={onUnpublish}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <SubmitToEpicDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        sprite={top}
      />
    </section>
  );
}

function PublicSpriteCard({ sprite, rank, userId, onVote, onUnpublish }) {
  const variant = VARIANTS[sprite.variant] || VARIANTS.base;
  const hasVoted = sprite.votes?.includes(userId);
  const isOwner = sprite.owner_id === userId;

  const handleVote = async () => {
    try {
      await onVote(sprite.id, userId);
    } catch (e) {
      toast({ title: 'Vote failed', description: e.message });
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your sprite from the public gallery?')) return;
    try {
      await onUnpublish(sprite.id, userId);
      toast({ title: 'Unpublished', description: 'Your sprite is back to private.' });
    } catch (e) {
      toast({ title: 'Could not unpublish', description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div
      className="relative rounded-2xl border-[3px] overflow-hidden shadow-[0_5px_0_var(--t-card-border)] hover:-translate-y-1 transition-transform"
      style={{
        background: 'var(--t-card)',
        borderColor: rank === 1 ? '#ffb800' : 'var(--t-card-border)',
      }}
    >
      <span
        className="absolute top-2 left-2 z-10 inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-md text-[11px] font-black"
        style={{
          background: rank === 1 ? '#ffd900' : rank <= 3 ? '#ff2bd6' : '#3a1064',
          color: rank === 1 ? '#3a1064' : '#fff',
          border: '2px solid #3a1064',
          boxShadow: '0 2px 0 #3a1064',
        }}
      >
        #{rank}
      </span>
      <span
        className="absolute top-2 right-2 z-10 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded text-white border border-white/40"
        style={{ background: variant.color }}
      >
        {variant.label}
      </span>

      <div
        className="aspect-square flex items-center justify-center"
        style={{
          background: `radial-gradient(circle at center, ${variant.color}33 0%, transparent 70%), linear-gradient(180deg, #faf7ff 0%, #f3edff 100%)`,
        }}
      >
        <img
          src={sprite.data_url}
          alt={sprite.name}
          className="h-[82%] w-[82%] object-contain drop-shadow-[0_6px_8px_rgba(58,16,100,0.35)]"
        />
      </div>

      <div className="p-3" style={{ background: 'var(--t-card)' }}>
        <div className="font-black text-sm truncate" style={{ color: 'var(--t-text)' }}>{sprite.name}</div>
        <div className="text-[11px] font-bold mt-0.5 truncate" style={{ color: 'var(--t-text-soft)' }}>
          by {sprite.owner_name}
        </div>

        <div className="mt-2 flex items-center gap-1.5">
          <button
            onClick={handleVote}
            className={`flex-1 h-9 rounded-xl border-2 text-[11px] font-black uppercase tracking-wide transition-all flex items-center justify-center gap-1.5 shadow-[0_3px_0_var(--t-card-border)] ${
              hasVoted ? 'bg-[#ff2bd6] text-white' : 'bg-white hover:bg-yellow-50 text-[#3a1064]'
            }`}
            style={{ borderColor: 'var(--t-card-border)' }}
          >
            <ThumbsUp className="h-3.5 w-3.5" fill={hasVoted ? 'currentColor' : 'none'} />
            {sprite.vote_count}
          </button>
          {isOwner && (
            <button
              onClick={handleRemove}
              className="h-9 w-9 rounded-xl border-2 border-rose-300 bg-white hover:bg-rose-50 text-rose-600 flex items-center justify-center"
              title="Unpublish my sprite"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmitToEpicDialog({ open, onOpenChange, sprite }) {
  if (!sprite) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] w-[calc(100vw-16px)] max-h-[92vh] p-0 border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] overflow-y-auto">
        <div
          className="p-5 border-b-[3px] border-[#3a1064]"
          style={{ background: 'linear-gradient(135deg, #ffd900 0%, #ff9a1c 100%)' }}
        >
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-[#3a1064] font-black text-2xl flex items-center gap-2">
              <Trophy className="h-6 w-6" fill="#3a1064" /> Submit to Epic Games
            </DialogTitle>
            <DialogDescription className="text-[#3a1064]/85 font-bold">
              Send the community&apos;s top-voted sprite to Epic for consideration in a future Fortnite drop.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-5 bg-white space-y-4">
          <div className="flex items-center gap-4 rounded-xl p-3 border-2 border-[#3a1064] bg-[#fff7d6]">
            <img src={sprite.data_url} alt={sprite.name} className="h-20 w-20 object-contain" />
            <div>
              <div className="font-black text-[#3a1064]">{sprite.name}</div>
              <div className="text-xs font-bold text-[#3a1064]/80">by {sprite.owner_name}</div>
              <div className="mt-1 inline-flex items-center gap-1.5 bg-[#3a1064] text-yellow-300 px-2 py-0.5 rounded-full font-black text-[11px]">
                <ThumbsUp className="h-3 w-3" fill="currentColor" /> {sprite.vote_count} community votes
              </div>
            </div>
          </div>
          <div className="rounded-xl border-2 border-[#3a1064] bg-[#fff7d6] p-3 text-sm font-bold text-[#3a1064]">
            <span className="font-black uppercase tracking-wider text-[11px] block mb-1">Heads up</span>
            Sprite Vault is a fan-made site, not affiliated with Epic Games. The &quot;Submit to Epic&quot; feature is conceptual — clicking below will copy the sprite&apos;s details to your clipboard so you can share it on socials, tag <span className="text-[#ff2bd6]">@FortniteGame</span>, and rally the community behind it.
          </div>
        </div>
        <DialogFooter className="p-5 pt-0 bg-white gap-2 flex-wrap">
          <Button
            variant="outline"
            className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
            onClick={async () => {
              const text =
                `🎮 Hey @FortniteGame — the community wants this sprite in the game!\n` +
                `"${sprite.name}" by ${sprite.owner_name}\n` +
                `${sprite.vote_count} votes on Sprite Vault\n` +
                `${window.location.origin}`;
              try {
                await navigator.clipboard.writeText(text);
                toast({ title: 'Submission text copied!', description: 'Paste it on X / TikTok / Reddit and tag Epic.' });
              } catch (e) {
                toast({ title: 'Copy failed', description: 'Try again or share manually.' });
              }
              onOpenChange(false);
            }}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Copy submission post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
